/**
 * 数据诊断公式图表 builder
 * 按系统折线图设定：当期实线+面积、往期虚线
 */
import vueI18n from '@/i18n/locale'

import {
  XAxisBuilder,
  YAxisBuilder,
  ToolTipBuilder,
  getEchartHeightAndBottomByLocale,
} from './builderBR.js'
import { designPxToRootPx, designPxToRem } from './designPxToRem.js'
import caculateWidthBy10FontSize from '@/pages-v2/ShopDetail/components/DiagnosisCommon/utils/caculateFontSize.js'

/** 折线下方面积：与 CSS `linear-gradient(180deg, rgba(51,136,255,0.08) 0%, rgba(51,136,255,0) 100%)` 等效 */
// const FORMULA_AREA_GRADIENT = {
//   type: 'linear',
//   x: 0,
//   y: 0,
//   x2: 0,
//   y2: 1,
//   colorStops: [
//     { offset: 0, color: 'rgba(51, 136, 255, 0.08)' },
//     { offset: 1, color: 'rgba(51, 136, 255, 0)' },
//   ],
// }

const FORMULA_EXPOSURE_LEGEND_ITEMS = t => [
  {
    name: t('FoodB_requirement_aEIJ_Ryni' /** 当前周期 */),
    type: 'line',
    color: '#0D86FF',
  },
  {
    name: t('FoodB_20230410_Qmmi_eYts' /** 上期 */),
    type: 'line',
    color: '#85C2FF',
  },
]

function createFormulaExposureTooltip(direction = 'vertical') {
  const r = designPxToRem

  // 竖屏使用ToolTipBuilder
  if (direction === 'vertical') {
    return ToolTipBuilder({
      formulaExposureMarker: true,
      position: {
        bottomAnchorOffset: designPxToRootPx(-20),
      },
      axisPointer: {
        type: 'none',
        snap: true,
        label: { show: false },
        lineStyle: {
          color: '#0D86FF',
          width: 1,
          type: 'dashed',
          opacity: 0.7,
        },
      },
    })
  }

  // 横屏使用自定义tooltip配置
  return {
    show: true,
    trigger: 'axis',
    axisPointer: {
      type: 'none',
      snap: true,
      label: { show: false },
      lineStyle: {
        color: '#0D86FF',
        width: 1,
        type: 'dashed',
        opacity: 0.7,
      },
    },
    backgroundColor: '#000',
    borderWidth: 0,
    padding: [0, 0, 0, 0],
    extraCssText: `border-radius:${r(16)};opacity:1;backdrop-filter:blur(${r(
      54.4,
    )});-webkit-backdrop-filter:blur(${r(54.4)});box-shadow:0 ${r(31.67)} ${r(
      160,
    )} 0 rgba(0,0,0,0.06);max-width:90vw;`,
    position: function (point, _params, _dom, _rect, size) {
      const [pointX] = point || [0]
      const [contentWidth = 0, contentHeight = 0] = size?.contentSize || []
      const [viewWidth = 0, viewHeight = 0] = size?.viewSize || []
      const edgeGap = 8

      // 横向位置：跟随鼠标
      let left = pointX + edgeGap
      if (left + contentWidth > viewWidth - edgeGap) {
        left = pointX - contentWidth - edgeGap
      }
      left = Math.max(edgeGap, Math.min(left, viewWidth - contentWidth - edgeGap))

      // 纵向位置：贴底显示，让tooltip底部对齐x轴
      const top = viewHeight - contentHeight - 44

      return [left, top]
    },
    formatter: function (params) {
      const list = Array.isArray(params) ? params : [params]
      const date = list[0]?.axisValue || list[0]?.name || ''
      const content = list
        .map((item, index) => {
          const { seriesName, value, seriesIndex } = item
          const marginBottom = index === list.length - 1 ? 0 : r(8)
          const dotColor = seriesIndex === 0 ? '#0D86FF' : '#85C2FF'
          const marker = `<span style="display:inline-block;width:${r(20)};height:${r(
            20,
          )};border-radius:50%;background:${dotColor};vertical-align:middle;margin-right:${r(
            8,
          )};flex-shrink:0;"></span>`
          return `<div style="display:flex;align-items:center;line-height:${r(
            32,
          )};margin-bottom:${marginBottom};"><div style="display:flex;align-items:center;width:${r(
            192,
          )};font-family: DiDi Sans Pro;flex-shrink:0;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;color:#89898c;">${marker}<span style="overflow:hidden;white-space:nowrap;text-overflow:ellipsis;color:#89898c;">${seriesName}</span></div><span style="flex:1;text-align:right;white-space:nowrap;font-weight:700;color:#fff;">${value}</span></div>`
        })
        .join('')
      return `<div style="padding:${r(24)};"><div style="line-height:${r(32)};margin-bottom:${r(
        8,
      )};text-align:left;">${date}</div>${content}</div>`
    },
  }
}

function isSharedFormulaChart(metricKey = '', metricName = '') {
  const key = String(metricKey || '').trim()
  const name = String(metricName || '').trim()
  return (
    key === 'dailyUVCnt' ||
    ['p1d30', 'p2d30', 'p4d30'].includes(key) ||
    ['曝光', '曝光-进店转化率', '进店-点击支付转化率', '点击支付-完单转化率'].includes(name)
  )
}

// function formatTooltipDateLabel(dateStr) {
//   if (dateStr == null || dateStr === '') return '--'
//   const s = String(dateStr).trim()
//   if (s.length >= 10 && s.charAt(4) === '-') return s.substring(5, 10)
//   return s
// }

// function escapeHtml(s) {
//   return String(s)
//     .replace(/&/g, '&amp;')
//     .replace(/</g, '&lt;')
//     .replace(/>/g, '&gt;')
//     .replace(/"/g, '&quot;')
// }

// function appendUnitForTooltip(value, unit) {
//   if (value == null || value === '' || value === '--') return '--'
//   const text = String(value)
//   if (!unit) return text
//   return text.includes(unit) ? text : `${text}${unit}`
// }

// function buildFormulaTooltip(current, previous) {
//   const r = designPxToRem
//   const off = designPxToRootPx(12)
//   return {
//     show: true,
//     trigger: 'axis',
//     /** 嵌入/全屏都展示竖向指示线，提升点击/滑动取值定位感 */
//     axisPointer: {
//       type: 'line',
//       snap: true,
//       label: { show: false },
//       lineStyle: {
//         color: '#0D86FF',
//         width: designPxToRootPx(2),
//         type: 'dashed',
//         opacity: 0.7,
//       },
//     },
//     renderMode: 'html',
//     backgroundColor: '#12121A',
//     borderWidth: 0,
//     padding: [0, 0, 0, 0],
//     extraCssText: `border-radius:${r(16)};opacity:1;backdrop-filter:blur(${r(
//       54.4,
//     )});-webkit-backdrop-filter:blur(${r(54.4)});box-shadow:0 ${r(31.67)} ${r(
//       160,
//     )} 0 rgba(0,0,0,0.06);max-width:90vw;`,
//     position(point, _params, _dom, _rect, size) {
//       const contentW = size?.contentSize?.[0] || 0
//       const contentH = size?.contentSize?.[1] || 0
//       const viewW = size?.viewSize?.[0] || 0
//       const viewH = size?.viewSize?.[1] || 0
//       const minGap = off

//       // 默认策略：显示在触点右上方
//       let x = point[0] + off
//       let y = point[1] - contentH - off

//       // 右侧空间不足则切到左侧
//       if (x + contentW > viewW - minGap) {
//         x = point[0] - contentW - off
//       }
//       // 顶部空间不足则切到下方
//       if (y < minGap) {
//         y = point[1] + off
//       }
//       // 下方也放不下时，贴底显示
//       if (y + contentH > viewH - minGap) {
//         y = viewH - contentH - minGap
//       }

//       // 最终夹紧，保证不越界
//       x = Math.max(minGap, Math.min(x, viewW - contentW - minGap))
//       y = Math.max(minGap, Math.min(y, viewH - contentH - minGap))
//       return [x, y]
//     },
//     formatter(params) {
//       const list = Array.isArray(params) ? params : [params]
//       const blocks = list.map((item, i) => {
//         const idx = item.dataIndex
//         const si = item.seriesIndex
//         const row = si === 0 ? current[idx] : previous[idx]
//         const dateLabel = formatTooltipDateLabel(row && row.date)
//         const unit = row && row.unit ? row.unit : ''
//         const v = appendUnitForTooltip(item.value, unit)
//         const dotHtml =
//           si === 0
//             ? `<span style="display:inline-block;vertical-align:middle;width:${r(10)};height:${r(
//                 10,
//               )};border-radius:50%;background:#0D86FF;flex-shrink:0;"></span>`
//             : `<span style="display:inline-block;vertical-align:middle;width:${r(10)};height:${r(
//                 10,
//               )};border-radius:50%;border:${r(
//                 2,
//               )} solid #0D86FF;background:transparent;box-sizing:border-box;flex-shrink:0;"></span>`
//         const mb = i < list.length - 1 ? `margin-bottom:${r(16)};` : ''
//         return `<div style="${mb}">
//           <div style="display:flex;align-items:center;gap:${r(6)};">
//             ${dotHtml}
//             <span style="color:#FFFFFF;font-size:${r(28)};line-height:${r(
//           36,
//         )};letter-spacing:0;font-family:DiDiSans-Pro-Regular;font-weight:normal;">${escapeHtml(
//           dateLabel,
//         )}</span>
//           </div>
//           <div style="margin-top:${r(8)};padding-left:${r(16)};color:#FFFFFF;font-size:${r(
//           32,
//         )};line-height:${r(
//           40,
//         )};letter-spacing:0;font-family:DiDiSans-Pro-Bold;font-weight:bold;">${escapeHtml(v)}</div>
//         </div>`
//       })
//       return `<div style="min-width:${r(120)};padding:${r(24)};">${blocks.join('')}</div>`
//     },
//   }
// }

/**
 * 构建公式指标折线图 option
 * @param {Object} chartData - { current: [{date, value}], previous: [{date, value}] }
 * @param {string} metricName - 指标名称（保留参数兼容调用方，图表内不再展示）
 * @param {Object} [options]
 * @param {boolean} [options.embedded=true] - 非全屏嵌入：隐藏 X/Y 轴文字与轴线，保留 Y 轴网格线；全屏传 `embedded: false` 恢复完整轴能力
 * @param {boolean} [options.fullscreenComfort=false] - 全屏专用：在四边 grid 上额外留白，避免贴边
 * @param {string} [options.direction='vertical'] - 图表方向：'vertical' 或 'horizontal'
 * @returns {Object} ECharts option
 */
export function buildFormulaChartOption(chartData = {}, metricName = '', options = {}) {
  const embedded = options.embedded !== false
  const metricKey = options.metricKey || ''
  const direction = options.direction || 'vertical'
  const isSharedFormula = isSharedFormulaChart(metricKey, metricName)
  const current = chartData.current || chartData.p1 || []
  const previous = chartData.previous || chartData.p2 || []
  const xAxisData = (current.length ? current : previous).map(item => item.date || item)
  const extractValue = item => {
    if (item.value != null) return item.value
    if (typeof item === 'number') return item
    return '--'
  }
  const currentValues = current.map(extractValue)
  const previousValues = previous.map(extractValue)

  // 根据数据最大值动态计算左侧间距，保证所有公式图表左侧对齐一致
  const allValues = currentValues.concat(previousValues).filter(v => v !== '--')
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0
  const maxValueStr = String(Math.ceil(maxValue))
  const baseWidth = maxValueStr
    ? caculateWidthBy10FontSize(maxValueStr)
    : caculateWidthBy10FontSize(0, 5)
  const left = baseWidth + 10

  const formulaEchartConfig = getEchartHeightAndBottomByLocale('usersAndConversion')
  let grid
  if (direction === 'horizontal') {
    grid = {
      top: 26,
      left: left + 16,
      right: 0,
      bottom: formulaEchartConfig.bottomHor,
      containLabel: false,
    }
  } else {
    grid = {
      top: 26,
      left,
      right: 8,
      bottom: formulaEchartConfig.bottom,
      containLabel: false,
    }
  }
  return {
    // title:
    //   !embedded && metricName
    //     ? {
    //         text: metricName,
    //         left: designPxToRootPx(32),
    //         top: designPxToRootPx(24),
    //         textStyle: {
    //           fontFamily: 'DiDiSans-Pro-Bold',
    //           fontSize: designPxToRootPx(36),
    //           fontWeight: 'bold',
    //           lineHeight: designPxToRootPx(48),
    //           letterSpacing: 0,
    //           color: '#0c0c0d',
    //         },
    //       }
    //     : { show: false },
    grid,
    // tooltip: createFormulaExposureTooltip(),
    tooltip: createFormulaExposureTooltip(direction),
    legend: { show: false },
    xAxis: [
      (() => {
        const xBase = XAxisBuilder({
          data: xAxisData.map(d =>
            typeof d === 'string' && d.length > 5 ? d.substring(5) : String(d || ''),
          ),
        })
        return {
          ...xBase,
          axisLabel: {
            ...xBase.axisLabel,
          },
          ...(embedded && !isSharedFormula
            ? {
                axisLine: { show: false },
              }
            : {}),
        }
      })(),
    ],
    yAxis: [
      (() => {
        const yBase = YAxisBuilder({ name: '' })
        return {
          ...yBase,
          nameTextStyle: {
            ...yBase.nameTextStyle,
          },
          axisLabel: {
            ...yBase.axisLabel,
          },
        }
      })(),
    ],
    series: [
      {
        name: vueI18n.t('FoodB_requirement_aEIJ_Ryni' /** 当前周期 */),
        type: 'line',
        showSymbol: false,
        yAxisIndex: 0,
        color: '#0D86FF',
        lineStyle: {
          width: designPxToRootPx(4),
          color: '#0D86FF',
        },
        emphasis: {
          disabled: true,
          itemStyle: {
            color: '#0D86FF',
          },
        },
        // areaStyle: {
        //   color: FORMULA_AREA_GRADIENT,
        // },
        data: currentValues,
      },
      {
        name: vueI18n.t('FoodB_20230410_Qmmi_eYts' /** 上期 */),
        type: 'line',
        showSymbol: false,
        yAxisIndex: 0,
        color: '#85C2FF',
        lineStyle: {
          width: designPxToRootPx(4),
          color: '#85C2FF',
        },
        emphasis: {
          disabled: true,
          itemStyle: {
            color: '#85C2FF',
          },
        },
        data: previousValues,
      },
    ],
    legendItems: FORMULA_EXPOSURE_LEGEND_ITEMS(vueI18n.t.bind(vueI18n)),
    showCustomLegend: true,
  }
}
